import * as React from 'react'
import { createPortal } from 'react-dom'

import { Toast, ToastDescription, ToastTitle, type ToastVariant } from '@/components/ui/toast'
import { Z } from '@/lib/zIndex'

/**
 * <ToastProvider> — the project's toast system (dep-free; not Radix).
 *
 * Mount once near the app root (src/main.tsx). It owns the toast queue + the
 * auto-dismiss timers and renders a single stacking <Toaster> via portal at
 * `Z.toast` (80). Any descendant can fire a toast via `useToast()`:
 *
 *   const toast = useToast()
 *   toast.error('Could not save')            // red, assertive
 *   toast.warning('Rate limited', { duration: 3000 })
 *   toast.success('Saved')
 *   const id = toast.toast({ title: 'Hi', variant: 'info' })
 *   toast.dismiss(id)                        // dismiss one (or all, with no arg)
 *
 * Replaces the hand-rolled `createPortal` error toasts (shared `ErrorToast` and
 * the duplicated amber inline variant in the procurement pages) with one
 * consistent, accessible, stacking primitive.
 */

export interface ToastOptions {
  variant?: ToastVariant
  /** Bold first line. */
  title?: React.ReactNode
  /** Supporting line under the title. */
  description?: React.ReactNode
  /** Auto-dismiss after this many ms; `0` keeps it sticky. Default 6000. */
  duration?: number
  /** Override the variant's default icon; `false` renders none. */
  icon?: React.ReactNode | false
  /** Accessible label for the close button. */
  closeLabel?: string
  /** Optional action button (e.g. "Reload" on update-consent toasts). */
  action?: { label: React.ReactNode; onClick: () => void }
}

interface ToastRecord {
  id: string
  variant: ToastVariant
  title?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode | false
  closeLabel?: string
  action?: ToastOptions['action']
  duration: number
}

const DEFAULT_DURATION = 6000

export interface ToastApi {
  /** Show a toast with full control. Returns its id (for `dismiss`). */
  toast: (options: ToastOptions) => string
  /** Dismiss one toast by id, or all if no id is given. */
  dismiss: (id?: string) => void
  error: (message: React.ReactNode, options?: Omit<ToastOptions, 'variant' | 'title'>) => string
  warning: (message: React.ReactNode, options?: Omit<ToastOptions, 'variant' | 'title'>) => string
  success: (message: React.ReactNode, options?: Omit<ToastOptions, 'variant' | 'title'>) => string
  info: (message: React.ReactNode, options?: Omit<ToastOptions, 'variant' | 'title'>) => string
}

const ToastContext = React.createContext<ToastApi | null>(null)

let idCounter = 0
const nextId = () => `toast-${++idCounter}`

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([])
  const timers = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const clearTimer = React.useCallback((id: string) => {
    const handle = timers.current.get(id)
    if (handle) {
      clearTimeout(handle)
      timers.current.delete(id)
    }
  }, [])

  const dismiss = React.useCallback(
    (id?: string) => {
      if (id === undefined) {
        timers.current.forEach((handle) => clearTimeout(handle))
        timers.current.clear()
        setToasts([])
        return
      }
      clearTimer(id)
      setToasts((curr) => curr.filter((t) => t.id !== id))
    },
    [clearTimer],
  )

  const toast = React.useCallback(
    (options: ToastOptions) => {
      const id = nextId()
      const record: ToastRecord = {
        id,
        variant: options.variant ?? 'default',
        title: options.title,
        description: options.description,
        icon: options.icon,
        closeLabel: options.closeLabel,
        action: options.action,
        duration: options.duration ?? DEFAULT_DURATION,
      }
      setToasts((curr) => [...curr, record])
      if (record.duration > 0) {
        timers.current.set(id, setTimeout(() => dismiss(id), record.duration))
      }
      return id
    },
    [dismiss],
  )

  React.useEffect(
    () => () => {
      timers.current.forEach((handle) => clearTimeout(handle))
      timers.current.clear()
    },
    [],
  )

  const api = React.useMemo<ToastApi>(() => {
    const ofVariant =
      (variant: ToastVariant) =>
      (message: React.ReactNode, options?: Omit<ToastOptions, 'variant' | 'title'>) =>
        toast({ variant, title: message, ...options })
    return {
      toast,
      dismiss,
      error: ofVariant('error'),
      warning: ofVariant('warning'),
      success: ofVariant('success'),
      info: ofVariant('info'),
    }
  }, [toast, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && toasts.length > 0
        ? createPortal(
            // Viewport: top-center stack. `pointer-events-none` so only the
            // toasts themselves (pointer-events-auto) catch clicks; `pt-safe`
            // clears the notch. Each toast gets a stacking-safe entrance.
            <div
              aria-label="Notifications"
              className={`fixed top-4 left-1/2 z-[${Z.toast}] flex w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 flex-col gap-2 pt-safe pointer-events-none`}
            >
              {toasts.map((t) => (
                <Toast
                  key={t.id}
                  variant={t.variant}
                  icon={t.icon}
                  onClose={() => dismiss(t.id)}
                  closeLabel={t.closeLabel}
                  action={t.action}
                  className="animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  {t.title != null ? <ToastTitle>{t.title}</ToastTitle> : null}
                  {t.description != null ? <ToastDescription>{t.description}</ToastDescription> : null}
                </Toast>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider> (mount it near the app root).')
  }
  return ctx
}

export default ToastProvider
